// ─── widgets/text.js ──────────────────────────────────────────────────────────
// Creates cleverstory.v2.widgets.text widget objects.

import shortid from 'shortid';
import {
    cs, getAbsoluteRect, hasLayoutBox,
    getFontFamily, getFontSize, getFontWeight, getLineHeight,
    getLetterSpacing, getTextTransform, getColor, getAlignment, getOverflow,
    getTextTypeAndLevel, isVisible, getTextContentWithLineBreaks,
} from '../utils/dom.js';
import { resolveFontFamily, normalizeFontFamily } from '../utils/fonts.js';
import { getTextContentWithLinks, linkWholeContent } from '../utils/links.js';
import { TEXT } from 'constants/widgetTypes.jsx';

// ── Style extractor ───────────────────────────────────────────────────────────
function extractStylesFromEl(el) {
    return {
        text:          getTextContentWithLineBreaks(el),
        fontFamily:    getFontFamily(el),
        fontSize:      getFontSize(el),
        fontWeight:    getFontWeight(el),
        lineHeight:    getLineHeight(el),
        letterSpacing: getLetterSpacing(el),
        textTransform: getTextTransform(el),
        color:         getColor(el),
        textAlign:     getAlignment(el),
        overflow:      getOverflow(el),
    };
}

function getListTypography(listEl) {
    const firstLi = Array.from(listEl.querySelectorAll('li')).find(isVisible);
    const source = firstLi || listEl;
    return {
        fontSize:   getFontSize(source),
        lineHeight: getLineHeight(source),
    };
}

function toLineSpacing(lineHeightPx, fontSizePx) {
    const raw = lineHeightPx / fontSizePx;
    // Preserve intentional tight leading (e.g. 0.93); only fallback for invalid near-zero ratios
    return raw >= 0.5 ? raw : 1.2;
}

// ── List HTML builder ─────────────────────────────────────────────────────────
function buildListHtml(listEl, fontFamilyMap, fontIdMap, actions) {
    const p = extractStylesFromEl(listEl);
    const { fontSize: listFontSize, lineHeight: listLineHeight } = getListTypography(listEl);
    const { resolvedFamily } = resolveFontFamily(p.fontFamily, fontFamilyMap, fontIdMap);
    let items = '';

    listEl.querySelectorAll('li').forEach((li) => {
        if (!isVisible(li)) return;
        const lp = extractStylesFromEl(li);
        if (!lp.text) return;
        // font-size intentionally omitted — inherited from <ul> for desktop,
        // scaled and written by TextMobileView.processContent for mobile
        const s = [
            `color:${lp.color ?? p.color ?? 'inherit'}`,
            `font-weight:${lp.fontWeight ?? p.fontWeight ?? 'normal'}`,
            `text-align:${lp.textAlign ?? p.textAlign ?? 'left'}`,
            `overflow:${lp.overflow ?? p.overflow ?? 'visible'}`,
        ].join(';') + ';';
        items += `<li style="${s}">${getTextContentWithLinks(li, actions)}</li>`;
    });

    const ff = normalizeFontFamily(resolvedFamily || p.fontFamily);
    const ws = [
        `font-family:${ff}`,
        `font-size:${listFontSize ?? 16}px`,   // ← back on <ul> only, drives desktop + ratio base
        `color:${p.color ?? 'inherit'}`,
        `font-weight:${p.fontWeight ?? 'normal'}`,
        `text-align:${p.textAlign ?? 'left'}`,
        `letter-spacing:${p.letterSpacing ?? 0}px`,
    ].join(';') + ';';
    // line-height stays omitted — controlled by widget lineSpacing property

    return `<${listEl.tagName.toLowerCase()} style="${ws}">${items}</${listEl.tagName.toLowerCase()}>`;
}

// ── Inline HTML builder ───────────────────────────────────────────────────────
function buildTextHtml(rawText, type, level, fontFamily, fontSize, color, fontWeight, overflow, letterSpacing, textTransform) {
    const ff = normalizeFontFamily(fontFamily);
    const style = [
        `font-size:${fontSize ?? 16}px`,
        `font-family:${ff}`,
        `color:${color ?? 'inherit'}`,
        `font-weight:${fontWeight ?? 'normal'}`,
        `overflow:${overflow ?? 'visible'}`,
        `letter-spacing:${letterSpacing ?? 0}px`,
        `text-transform:${textTransform ?? 'none'}`,
    ].join(';') + ';';

    if (type === 'heading') return `<h${level} style="${style}">${rawText}</h${level}>`;
    if (type === 'list')    return rawText;
    return `<p style="${style}">${rawText}</p>`;
}

// ── Mixed content builder (text wrapper containing both <p>/<h*> and <ul>/<ol>) ─
function buildMixedHtml(el, fontFamilyMap, fontIdMap, actions) {
    const wp = extractStylesFromEl(el);
    const { resolvedFamily: wrf } = resolveFontFamily(wp.fontFamily, fontFamilyMap, fontIdMap);
    const wff = normalizeFontFamily(wrf || wp.fontFamily);

    let html = '';

    el.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const t = node.textContent.trim();
            if (!t) return;
            const style = [
                `font-size:${wp.fontSize ?? 16}px`,
                `font-family:${wff}`,
                `color:${wp.color ?? 'inherit'}`,
                `font-weight:${wp.fontWeight ?? 'normal'}`,
                `overflow:${wp.overflow ?? 'visible'}`,
                `letter-spacing:${wp.letterSpacing ?? 0}px`,
                `text-transform:${wp.textTransform ?? 'none'}`,
            ].join(';') + ';';
            html += `<p style="${style}">${t}</p>`;
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();

        if (tag === 'ul' || tag === 'ol') {
            html += buildListHtml(node, fontFamilyMap, fontIdMap, actions);
        } else {
            const np = extractStylesFromEl(node);
            const { resolvedFamily: nrf } = resolveFontFamily(np.fontFamily, fontFamilyMap, fontIdMap);
            const nff = normalizeFontFamily(nrf || np.fontFamily || wrf || wp.fontFamily);

            const style = [
                `font-size:${np.fontSize ?? wp.fontSize ?? 16}px`,
                `font-family:${nff || wff}`,
                `color:${np.color ?? wp.color ?? 'inherit'}`,
                `font-weight:${np.fontWeight ?? wp.fontWeight ?? 'normal'}`,
                `overflow:${np.overflow ?? wp.overflow ?? 'visible'}`,
                `letter-spacing:${np.letterSpacing ?? wp.letterSpacing ?? 0}px`,
                `text-transform:${np.textTransform ?? wp.textTransform ?? 'none'}`,
            ].join(';') + ';';

            if (!node.textContent.trim()) return;
            // A bare <a> child is a link in its own right; otherwise keep any links inside it
            const t = tag === 'a'
                ? linkWholeContent(node, getTextContentWithLinks(node, actions), actions)
                : getTextContentWithLinks(node, actions);

            const headingMatch = tag.match(/^h([1-6])$/);
            if (headingMatch) {
                html += `<h${headingMatch[1]} style="${style}">${t}</h${headingMatch[1]}>`;
            } else {
                html += `<p style="${style}">${t}</p>`;
            }
        }
    });

    return html;
}

// ── Mobile inner-child resolver ───────────────────────────────────────────────
// Given a desktop wrapper + the specific inner child being processed, finds the
// matching child in the mobile clone by position index.
function resolveMobileInnerEl(mobileEl, desktopWrapperEl, desktopInnerEl) {
    if (!mobileEl || !desktopInnerEl) return mobileEl;

    const desktopChildren = Array.from(desktopWrapperEl.children).filter(
        (c) => isVisible(c) && c.textContent.trim().length > 0,
    );
    const idx = desktopChildren.indexOf(desktopInnerEl);

    const mobileChildren = Array.from(mobileEl.children).filter(
        (c) => isVisible(c) && c.textContent.trim().length > 0,
    );

    return mobileChildren[idx] ?? mobileEl;
}

// ── Child-split analyser (exported) ──────────────────────────────────────────
/**
 * Decides whether a [data-widget="text"] wrapper should be split into multiple
 * widgets — one per heading/paragraph child — instead of being emitted as a
 * single widget.
 *
 * Cases that trigger a split:
 *   • Wrapper has 2+ visible children that are ALL h1-h6, p, ul, or ol.
 *     Each child becomes its own TEXT widget with accurate styles + rect.
 *     Examples:
 *       <div data-widget="text"><h3>Title</h3><p>Body</p></div>
 *       <div data-widget="text"><h2>…</h2><p>…</p><ul>…</ul><p>…</p></div>
 *
 * Cases that do NOT split:
 *   • Single child                → createTextWidgetObj (single dominant child path)
 *   • Children include non-block elements (div, span, …) → falls through to isMixed
 *   • Raw text node wrapper with no element children
 *   • el is itself a ul/ol/li/h/p (leaf widget, not a wrapper)
 *
 * @param {Element} el  Desktop [data-widget="text"] element
 * @returns {Element[]|null}  Array of inner children to split into, or null
 */
export function resolveTextChildren(el) {
    const tag = el.tagName.toLowerCase();

    // Only analyse wrapper-style elements (div, span, section, article, …)
    // Never split bare heading/p/ul/ol elements — they are leaf widgets.
    if (/^(h[1-6]|p|ul|ol|li)$/.test(tag)) return null;

    const children = Array.from(el.children).filter(
        (c) => isVisible(c) && c.textContent.trim().length > 0,
    );

    // Need at least 2 children to justify splitting.
    if (children.length < 2) return null;

    // All children must be headings, paragraphs, or lists.
    // This covers: h2+p+ul+p, h3+p, h2+h4+p, etc.
    const allSplittable = children.every(
        (c) => /^(h[1-6]|p|ul|ol)$/.test(c.tagName.toLowerCase()),
    );
    if (!allSplittable) return null;

    return children;
}

// ── Core single-widget builder (internal) ────────────────────────────────────
// Builds one TEXT widget object from a specific source element (styleEl/rectEl).
// Used by both createTextWidgetObj (normal path) and createTextWidgetObjFromChild
// (split path).
function _buildTextWidget({
    styleEl, rectEl, mobileStyleEl, mobileRectEl,
    parentUid, offsetRect, mobileOffsetRect,
    orderRef, fontFamilyMap, fontIdMap,
    isList, isMixed, wrapperEl, // wrapperEl only needed for list/mixed builders
}) {
    const styles = extractStylesFromEl(styleEl);
    const rect   = getAbsoluteRect(rectEl);
    // Hidden on mobile (display:none) → no usable rect; fall back to the desktop
    // offset and keep the widget hidden there.
    const mobileHidden = !!mobileRectEl && !hasLayoutBox(mobileRectEl);
    const mRect  = mobileRectEl && !mobileHidden ? getAbsoluteRect(mobileRectEl) : null;

    let type, level, rawContent;
    // Link actions for every <a href> carried into the content as <a data-action>
    const actions = [];

    if (isList) {
        type = 'list'; level = null;
        rawContent = buildListHtml(wrapperEl, fontFamilyMap, fontIdMap, actions);

    } else if (isMixed) {
        type = 'body'; level = null;
        rawContent = buildMixedHtml(wrapperEl, fontFamilyMap, fontIdMap, actions);

    } else {
        // Derive type from the actual tag of styleEl
        const elTag = styleEl.tagName.toLowerCase();
        const headingMatch = elTag.match(/^h([1-6])$/);
        if (headingMatch) {
            type  = 'heading';
            level = parseInt(headingMatch[1], 10);
        } else if (elTag === 'p') {
            type  = 'body';
            level = null;
        } else if (/^(a|span|strong|em|b|i|label|small|cite|abbr)$/.test(elTag)) {
            // Inline/anchor element promoted as styleEl — always body text
            type  = 'body';
            level = null;
        } else {
            // Fallback heuristic for bare wrapper divs with no recognisable child
            const d = getTextTypeAndLevel(styles.fontSize);
            type = d.type; level = d.level;
        }
        rawContent = linkWholeContent(styleEl, getTextContentWithLinks(styleEl, actions), actions);
    }

    const { resolvedFamily, fontIds } = resolveFontFamily(styles.fontFamily, fontFamilyMap, fontIdMap);
    const rff = resolvedFamily || styles.fontFamily;

    const finalHtml = (isMixed || isList)
        ? rawContent
        : buildTextHtml(
            rawContent, type, level,
            rff, styles.fontSize, styles.color, styles.fontWeight,
            styles.overflow, styles.letterSpacing, styles.textTransform,
          );

    // Desktop position
    const x = Math.round(rect.left - (offsetRect.left || 0));
    const y = Math.round(rect.top  - (offsetRect.top  || 0));
    // const w = Math.round(rect.width + styles.fontSize * 0.5 + 4) + 'px';
    const w = rff === "Montserrat" ? Math.round(rect.width + 42) + 'px' :  Math.round(rect.width + 4) + 'px';
    // const h = Math.round(rect.height) + 'px';

    // Mobile position
    const mOff = mobileOffsetRect || offsetRect;
    const mx   = mRect ? Math.round(mRect.left - (mOff.left || 0)) : Math.min(x, 20);
    const my   = mRect ? Math.round(mRect.top  - (mOff.top  || 0)) : y;
    // Montserrat renders wider in the editor than it measures here (same reason
    // as the desktop +42), but on mobile only take the room left before the
    // viewport edge so full-width paragraphs don't overflow.
    let mPad = 6;
    if (mRect && rff === "Montserrat") {
        const viewportW = mobileRectEl.ownerDocument.documentElement.clientWidth;
        mPad = Math.max(6, Math.min(42, viewportW - (mRect.left + mRect.width)));
    }
    const mw   = mRect ? Math.round(mRect.width + mPad) + 'px' : Math.min(parseInt(w), 335) + 'px';
    // const mh   = mRect ? Math.round(mRect.height) + 'px' : h;

    const ms = mobileStyleEl
        ? extractStylesFromEl(mobileStyleEl)
        : extractStylesFromEl(mobileRectEl || styleEl);

    const { resolvedFamily: mrf, fontIds: mfi } = resolveFontFamily(ms.fontFamily, fontFamilyMap, fontIdMap);
    const mfs        = ms.fontSize.toString();
    const allFontIds = [...new Set([...fontIds, ...mfi])];

    const name = type === 'heading' ? `Heading ${level}` : type === 'list' ? 'List' : 'Paragraph';
    const listTypography = isList ? getListTypography(rectEl) : null;
    const mobileListTypography = isList && mobileRectEl && !mobileHidden ? getListTypography(mobileRectEl) : null;
    const desktopFontSizeForProperty = listTypography
    ? String(listTypography.fontSize)
    : styles.fontSize.toString();
    const mobileDesktopFontSizeForProperty = mobileListTypography
    ? String(mobileListTypography.fontSize)
    : ms.fontSize.toString();

    const lineHeightPx = listTypography ? listTypography.lineHeight : parseFloat(styles.lineHeight);
    const fontSizePx = listTypography ? listTypography.fontSize : parseFloat(styles.fontSize);
    const mobileLineHeightPx = mobileListTypography
        ? mobileListTypography.lineHeight
        : parseFloat(ms.lineHeight);
    const mobileFontSizePx = mobileListTypography
        ? mobileListTypography.fontSize
        : parseFloat(ms.fontSize);
    const lineHeightUnitless = toLineSpacing(lineHeightPx, fontSizePx);
    const mobileLineHeightUnitless = toLineSpacing(mobileLineHeightPx, mobileFontSizePx);
    
    return {
        uid:           `widget_${shortid.generate()}`,
        type:          TEXT,
        name,
        parent:        parentUid,
        order:         orderRef.value++,
        systemDefined: false,
        fonts:         allFontIds.length > 0 ? allFontIds : null,
        parameter:     null,
        actions,
        properties: {
            desktop: {
                visible: true,
                layout: {
                    transform: { translate: `${x}px,${y}px`, rotate: '0deg', scale: '1,1' },
                    width: w, height: 'auto',
                },
                fontSize:      desktopFontSizeForProperty,
                lineSpacing:   lineHeightUnitless,
                letterSpacing: 0,
                color:         null,
                alignment:     styles.textAlign,
                animation:     null,
                fontFamily:    rff || null,
            },
            mobile: {
                visible: !mobileHidden,
                layout: {
                    transform: { translate: `${mx}px,${my}px`, rotate: '0deg', scale: '1,1' },
                    width: mw, height: 'auto',
                },
                fontSize:      mobileDesktopFontSizeForProperty,
                lineSpacing:   mobileLineHeightUnitless,
                letterSpacing: 0,
                color:         null,
                alignment:     ms.textAlign,
                animation:     null,
                fontFamily:    mrf || ms.fontFamily || null,
            },
        },
        content: finalHtml,
    };
}

// ── Public API: split-child widget ───────────────────────────────────────────
/**
 * Creates a single TEXT widget for one specific inner child element of a wrapper.
 * Called by index.js when resolveTextChildren() returns a child array.
 *
 * @param {{ wrapperEl, mobileWrapperEl, childEl, childIndex,
 *           parentUid, offsetRect, mobileOffsetRect, orderRef,
 *           fontFamilyMap, fontIdMap }}
 */
export function createTextWidgetObjFromChild({
    wrapperEl, mobileWrapperEl, childEl, childIndex,
    parentUid, offsetRect, mobileOffsetRect, orderRef,
    fontFamilyMap, fontIdMap,
}) {
    // Find the matching mobile child by index
    const mobileChildren = mobileWrapperEl
        ? Array.from(mobileWrapperEl.children).filter(
            (c) => isVisible(c) && c.textContent.trim().length > 0,
          )
        : [];
    const mobileChildEl = mobileChildren[childIndex] ?? mobileWrapperEl ?? null;

    const childTag  = childEl.tagName.toLowerCase();
    const childIsList = childTag === 'ul' || childTag === 'ol';

    return _buildTextWidget({
        styleEl:        childIsList ? (mobileChildEl || childEl) : childEl,
        rectEl:         childEl,
        mobileStyleEl:  mobileChildEl,
        mobileRectEl:   mobileChildEl,
        parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap,
        isList:   childIsList,
        isMixed:  false,
        wrapperEl: childIsList ? childEl : undefined,  // list builder needs the ul/ol itself
    });
}

// ── Public API: standard widget creator ──────────────────────────────────────
/**
 * Creates a TEXT widget for a [data-widget="text"] element (or bare ul/ol).
 * For wrappers with a single h/p child, styles and rect are read from that
 * inner child for accuracy.  For multi-child heading+p wrappers, call
 * resolveTextChildren() + createTextWidgetObjFromChild() from index.js instead.
 *
 * @param {{ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }}
 */
export function createTextWidgetObj({
    el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap,
}) {
    const tag    = el.tagName.toLowerCase();
    const isList = tag === 'ul' || tag === 'ol';

    const innerList = !isList ? el.querySelector('ul, ol') : null;
    const isMixed   = !!innerList;

    // Single dominant h*/p child — promote it for accurate styles + rect
    const elTag = el.tagName.toLowerCase();
    const elIsLeaf = /^(h[1-6]|p|a|span|strong|em|b|i|label|small|cite|abbr|li)$/.test(elTag);
    let innerEl = null;
    if (!isList && !isMixed && !elIsLeaf)  {
        const children = Array.from(el.children).filter(
            (c) => isVisible(c) && c.textContent.trim().length > 0,
        );
        if (children.length === 1) {
            const child = children[0];
            const ctag  = child.tagName.toLowerCase();
            if (/^(h[1-6]|p|a|span|strong|em|b|i|label|small|cite|abbr)$/.test(ctag)) {
                innerEl = child;
            }
        }
    }

    const styleEl = innerEl || el;
    const rectEl  = innerEl || el;

    const mobileInnerEl = innerEl
        ? resolveMobileInnerEl(mobileEl, el, innerEl)
        : mobileEl;

    return _buildTextWidget({
        styleEl,
        rectEl,
        mobileStyleEl: mobileInnerEl,
        mobileRectEl:  mobileInnerEl,
        parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap,
        isList, isMixed,
        wrapperEl: el,   // needed by list/mixed builders
    });
}
